import { RadioGroup } from '@headlessui/react';
import React from 'react';
import Image, { ImageProps } from '../elements/image/image';
import { isNone, isSome, Maybe } from '@crocodille/common';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';

import styles from './horizontal-list-select.module.scss';
import { formatCurrency } from '@utils/format';

export type HorizontalListSelectChild<V> = React.ReactElement<
  HorizontalListSelectOptionProps<V>,
  typeof Option
>;

export interface HorizontalListSelectProps<V> {
  readonly value: V;
  onChange(value: V): void;
  readonly className?: Maybe<string>;
}

function HorizontalListSelect<V = unknown>({
  value,
  onChange,
  children,
  className,
}: React.PropsWithChildren<HorizontalListSelectProps<V>>) {
  return (
    <div className={styles.container}>
      <RadioGroup
        className={cn(styles.group, className)}
        value={value}
        onChange={onChange}
      >
        {children}
      </RadioGroup>
    </div>
  );
}

export interface HorizontalListSelectOptionProps<V> {
  readonly value: V;
  readonly disabled?: boolean;
  readonly image?: Maybe<ImageProps['src']>;
  readonly label: string;
  readonly description?: Maybe<string>;
  readonly price?: number;
  readonly priceAdjustment?: boolean;
}

function Option<V = unknown>({
  value,
  disabled,
  image,
  label,
  description,
  price,
  priceAdjustment,
  children,
}: React.PropsWithChildren<HorizontalListSelectOptionProps<V>>) {
  const {
    i18n: { language },
  } = useTranslation();

  return (
    <RadioGroup.Option
      value={value}
      disabled={disabled}
      className={({ active, checked, disabled }) =>
        cn(styles.option, {
          [styles.isActive]: active,
          [styles.isChecked]: checked,
          [styles.isDisabled]: disabled,
          [styles.isWithoutDescription]: isNone(description),
        })
      }
    >
      {({ checked, disabled }) => (
        <>
          <div
            className={cn(styles.imageContainer, {
              [styles.isChecked]: checked,
              [styles.isDisabled]: disabled,
            })}
          >
            <div className={cn(styles.image)}>
              <Image
                role="presentation"
                src={image}
                alt={label}
                width="150"
                height="150"
                sizes="150px"
                objectFit="contain"
              />
            </div>
            {children}
          </div>
          <span className={styles.label}>{label}</span>
          {isSome(description) ? (
            <span className={styles.description}>{description}</span>
          ) : null}
          {isSome(price) ? (
            <span className={styles.price}>
              <span className={styles.priceInner}>
                {formatCurrency(price, 'CZK', language, {
                  signDisplay: priceAdjustment ? 'exceptZero' : 'auto',
                })}
              </span>
            </span>
          ) : null}
        </>
      )}
    </RadioGroup.Option>
  );
}

export default Object.assign(HorizontalListSelect, { Option });
